import { DateTime } from 'luxon';

import { FORMAT_DATE } from './const';
import { calcStatusByPercentageCompletion, countBusinessDays, getDiffDayFromDateString } from './functions';
import { getMappingIdIndex } from './gantTransformData';
import { BondType, DraggableGantType, ItemType, MappingItemIdIndexType } from './types';

export const sumFieldForParent = (
    mappingItemIdIndex: MappingItemIdIndexType,
    parentId: string,
    oldItems: ItemType[],
): ItemType[] => {
    const oldParentItem = { ...oldItems[mappingItemIdIndex[parentId]] };
    const newItems = [...oldItems];

    const fieldForSum = [
        'countTools',
        'countHours',
        'countDays',
        'countPeople',
        'countMaterial',
        'valueOther',
        'valueTotal',
        'valueLabour',
        'valueEquipment',
        'valueMaterial',
        'materialHours',
        'equipmentHours',
    ] as (keyof ItemType)[];

    const sumField: { [key: string]: number } = {};

    oldParentItem.children?.forEach((childId) => {
        fieldForSum.forEach((keyField) => {
            const newValue = +(newItems[mappingItemIdIndex[childId]] as any)?.[keyField] || 0;
            if (newValue !== 0) {
                if (!sumField[keyField]) {
                    sumField[keyField] = 0;
                }
                sumField[keyField] += newValue;
            }
        });
    });

    newItems[mappingItemIdIndex[parentId]] = { ...oldParentItem, ...sumField };

    const newParentId = oldParentItem.parentId;
    if (newParentId) {
        return sumFieldForParent(mappingItemIdIndex, oldParentItem.parentId!, newItems);
    }

    return newItems;
};

export const changePercentageItemByPercent = (
    mappingItemIdIndex: MappingItemIdIndexType,
    newPercent: number,
    oldItem: ItemType,
    oldItems: ItemType[],
) => {
    const checkPercent = (percent: number) => {
        if (percent > 100) {
            return 100;
        } else if (percent < 0) {
            return 0;
        }
        return Math.round(percent);
    };

    const newItems = [...oldItems];
    const newCompletionPercentage = checkPercent(newPercent);
    newItems[mappingItemIdIndex[oldItem.id]] = {
        ...newItems[mappingItemIdIndex[oldItem.id]],
        completionPercentage: newCompletionPercentage,
        status: calcStatusByPercentageCompletion(newCompletionPercentage),
    };

    const changeParentPercentage = (parentId: string, bufferParentId: string[]) => {
        bufferParentId.push(parentId);
        const oldParentItem = newItems[mappingItemIdIndex[parentId]];

        let sumChildrenPercent = 0;
        const countChildrenItemWithoutDeletedItems = oldParentItem.children?.filter(
            (childId) => mappingItemIdIndex[childId],
        )?.length;

        if (!countChildrenItemWithoutDeletedItems || !oldParentItem.children) {
            return;
        }

        sumChildrenPercent = oldParentItem.children
            .map((childId) => {
                const childItem = newItems[mappingItemIdIndex[childId]];
                if (childItem) {
                    return childItem.countDay * (childItem?.completionPercentage || 0); // 3 days * 100%
                }
                return 0;
            })
            .reduce((a, b) => a + b);

        const sumChildrenCountDays = oldParentItem.children
            .map((childId) => newItems[mappingItemIdIndex[childId]]?.countDay || 0)
            .reduce((a, b) => a + b);

        //Progress ~ (3 days * 100% + 5 days * 30%) / 8 days
        const newPercent = sumChildrenPercent / sumChildrenCountDays;
        const newCompletionPercentage = checkPercent(newPercent);
        newItems[mappingItemIdIndex[parentId]] = {
            ...oldParentItem,
            completionPercentage: newCompletionPercentage,
            status: calcStatusByPercentageCompletion(newCompletionPercentage),
        };

        const newParentId = oldParentItem.parentId;
        if (newParentId && !bufferParentId.includes(newParentId)) {
            changeParentPercentage(oldParentItem.parentId!, bufferParentId);
        }
    };

    if (oldItem && oldItem.parentId) {
        changeParentPercentage(oldItem.parentId, []);
    }

    return newItems;
};

/*
 * Function calculate value of percent that update completion percentage
 * mappingItemIdIndex - object with position item in array. Key as id item and position index in array to fast access to element. Array need to change order of items
 * diffPixel - offset in pixel
 * oldItem - item that needs to move
 * oldItems - list items for move if we oldItem have dependencies with other item
 * widthColumn - width column Gantt chart on the screen
 * */

export const changePercentageItem = (
    mappingItemIdIndex: MappingItemIdIndexType,
    diffPixel: number,
    oldItem: ItemType,
    oldItems: ItemType[],
    widthColumn: number,
) => {
    const days = DateTime.fromISO(oldItem.endDate).diff(DateTime.fromISO(oldItem.startDate), 'days').days + 1;
    const sizeOnePercent = (days * widthColumn) / 100;
    const newPercent = oldItem.completionPercentage + diffPixel / sizeOnePercent;

    return changePercentageItemByPercent(mappingItemIdIndex, newPercent, oldItem, oldItems);
};

/*
 * Function calculate new position (start and end date) for each schedule items with dependencies and direction moving
 * mappingItemIdIndex - object with position item in array. Key as id item and position index in array to fast access to element. Array need to change order of items
 * diffDay - count of day for moving
 * dragging - what we move:
 *   - 'none' -
 *   - 'both' - move whole item
 *   - 'left' - move only left border of item
 *   - 'right' - move only right border of item
 *   - 'percentage' - move cursor for changing completion percentage
 * oldItem - item that needs to move
 * oldItems - list items for move if we oldItem have dependencies with other item
 * bufferMovedItem - list items tha was move for avoid infinity loop for loop dependencies
 * directionChanges - direction calculating data:
 *   - 'up' - calculate data from children to parent with recursion;
 *   - 'down' - calculate data from parent to children with recursion;
 *   - 'right' - calculate horizontal dependencies for bondRight;
 * */

export const changeItemsForMoving = (
    mappingItemIdIndex: MappingItemIdIndexType,
    diffDay: number,
    dragging: DraggableGantType,
    oldItem: ItemType,
    oldItems: ItemType[],
    bufferMovedItem: string[],
    directionChanges?: 'up' | 'down' | 'right',
): ItemType[] => {
    let newOldItems = directionChanges ? oldItems : [...oldItems];
    const newDiffDay = diffDay;

    if (!oldItem || bufferMovedItem.includes(oldItem.id)) {
        return newOldItems;
    }

    bufferMovedItem.push(oldItem.id);

    const getMaxMinDateFromItemList = (itemIds: string[]) => {
        let minDate = 10000000000;
        let maxDate = 0;

        itemIds.forEach((itemId) => {
            const subItem = newOldItems[mappingItemIdIndex[itemId]];
            if (!subItem) {
                return;
            }

            const start = DateTime.fromISO(subItem.startDate).toSeconds();
            if (start < minDate) {
                minDate = start;
            }

            const end = DateTime.fromISO(subItem.endDate).toSeconds();
            if (end > maxDate) {
                maxDate = end;
            }
        });

        return { minDate, maxDate };
    };

    const moveChildren = (itemIds: string[]) => {
        itemIds.forEach((itemId) => {
            const childItem = newOldItems[mappingItemIdIndex[itemId]];

            if (!childItem) {
                return;
            }

            newOldItems = changeItemsForMoving(
                mappingItemIdIndex,
                newDiffDay,
                'both',
                childItem,
                newOldItems,
                bufferMovedItem,
                'down',
            );

            if (childItem.bondRight) {
                moveBondedItem(childItem.bondRight, 0);
            }
        });
    };

    const moveBondedItem = (itemIds: string[], diffDay?: number) => {
        itemIds.forEach((itemId) => {
            const itemBond = newOldItems[mappingItemIdIndex[itemId]];

            if (itemBond) {
                newOldItems = changeItemsForMoving(
                    mappingItemIdIndex,
                    diffDay || newDiffDay,
                    'both',
                    itemBond,
                    newOldItems,
                    bufferMovedItem,
                    'right',
                );
                if (itemBond.parentId) {
                    newOldItems = moveParent(itemBond.parentId);
                }
            }
        });
    };

    const moveBondedByTypeItem = (item: ItemType, itemBondIds: string[], typeBond: BondType) => {
        itemBondIds.forEach((bondId) => {
            const bondItem = newOldItems[mappingItemIdIndex[bondId]];

            const getDiffDay = () => {
                switch (typeBond) {
                    case 'SS':
                        return getDiffDayFromDateString(item?.startDate!, bondItem.startDate);
                    case 'ES':
                        return getDiffDayFromDateString(item?.endDate!, bondItem.startDate) - 1;
                    case 'SE':
                        return getDiffDayFromDateString(item?.startDate!, bondItem.endDate) + 1;
                    case 'EE':
                        return getDiffDayFromDateString(item?.endDate!, bondItem.endDate);
                    default:
                        return 0;
                }
            };

            const diffDay = getDiffDay();

            if (diffDay <= 0) {
                newOldItems = changeItemsForMoving(
                    mappingItemIdIndex,
                    -diffDay,
                    'both',
                    bondItem,
                    newOldItems,
                    bufferMovedItem,
                    'right',
                );
            }
        });
    };

    const moveParent = (parentId: string) => {
        const filteredItem = newOldItems[mappingItemIdIndex[parentId]];

        if (filteredItem) {
            newOldItems = changeItemsForMoving(
                mappingItemIdIndex,
                newDiffDay,
                'both',
                filteredItem,
                newOldItems,
                bufferMovedItem,
                'up',
            );
            if (
                dragging !== 'left' &&
                filteredItem.bondRight &&
                filteredItem.endDate !== newOldItems[mappingItemIdIndex[parentId]].endDate
            ) {
                const diffDayBonded = getDiffDayFromDateString(
                    filteredItem.endDate,
                    newOldItems[mappingItemIdIndex[parentId]].endDate,
                );
                moveBondedItem(filteredItem.bondRight, diffDayBonded);
            }
            return newOldItems;
        }
        return newOldItems;
    };

    const moveItem = (currentStartDate: DateTime, item: ItemType): ItemType => {
        const fixItem: ItemType = { ...item };

        if (item.children?.length) {
            if (!directionChanges || directionChanges === 'down' || directionChanges === 'right') {
                moveChildren(item.children);
            }
            const { minDate, maxDate } = getMaxMinDateFromItemList(item.children);

            return {
                ...fixItem,
                startDate: DateTime.fromSeconds(minDate).toFormat(FORMAT_DATE),
                endDate: DateTime.fromSeconds(maxDate).toFormat(FORMAT_DATE),
            };
        }

        const getOffsetForWeekend = (date: DateTime) => {
            let offsetDaysForWeekend = 0;

            if (!item.onlyBusinessDay) {
                return offsetDaysForWeekend;
            }

            const weekendIndexStart = 7 - date.weekday;

            if (weekendIndexStart < 2) {
                offsetDaysForWeekend = newDiffDay >= 0 ? 2 : -2;
            }

            const extraOffset =
                (newDiffDay >= 3 && weekendIndexStart === 6 ? 1 : 0) +
                (newDiffDay <= -3 && weekendIndexStart === 2 ? -1 : 0);

            return offsetDaysForWeekend + extraOffset;
        };

        const newStartDate = currentStartDate.plus({ days: newDiffDay });
        const fixedStartDate = newStartDate.plus({
            days: getOffsetForWeekend(newStartDate),
        });

        fixItem.startDate = fixedStartDate.toFormat(FORMAT_DATE);

        let restOfCountDay = item.countDay;
        let newEndDate: DateTime = fixedStartDate;
        while (restOfCountDay > 1) {
            newEndDate = newEndDate.plus({ days: 1 });
            if (![6, 7].includes(newEndDate.weekday) || !item.onlyBusinessDay) {
                restOfCountDay--;
            }
        }

        if (newEndDate) {
            fixItem.endDate = newEndDate.toFormat(FORMAT_DATE);
        } else {
            fixItem.endDate = fixItem.startDate;
        }

        return fixItem;
    };

    const moveBorderItem = (typeEnd: 'startDate' | 'endDate', currentDate: DateTime, item: ItemType): ItemType => {
        const newDate = currentDate.plus({ days: newDiffDay });

        const lengthItemInDays =
            typeEnd === 'startDate'
                ? DateTime.fromISO(item.endDate).diff(newDate, 'days').days
                : newDate.diff(DateTime.fromISO(item.startDate), 'days').days;

        if (lengthItemInDays < 0) {
            return {
                ...item,
                [typeEnd]: typeEnd === 'startDate' ? item.endDate : item.startDate,
            };
        }

        const fixItem = {
            ...item,
            [typeEnd]: newDate.toFormat(FORMAT_DATE),
        };

        if (!item.onlyBusinessDay) {
            return fixItem;
        }

        let offsetDaysForWeekend = 0;

        const weekendIndex = 7 - newDate.weekday;
        if (weekendIndex < 2) {
            offsetDaysForWeekend = newDiffDay > 0 ? (weekendIndex ? 2 : 1) : weekendIndex ? -1 : -2;
        }
        fixItem[typeEnd] = newDate.plus({ days: offsetDaysForWeekend }).toFormat(FORMAT_DATE);

        return fixItem;
    };

    let newItem: ItemType | null = null;

    const start = DateTime.fromISO(oldItem.startDate);
    const end = DateTime.fromISO(oldItem.endDate);

    switch (dragging) {
        case 'both':
            newItem = moveItem(start, oldItem);
            break;
        case 'left':
            newItem = moveBorderItem('startDate', start, oldItem);
            break;
        case 'right':
            newItem = moveBorderItem('endDate', end, oldItem);
            break;
    }

    if (newItem) {
        if (dragging !== 'both') {
            if (newItem.onlyBusinessDay) {
                newItem.countDay = countBusinessDays(
                    DateTime.fromISO(newItem.startDate),
                    DateTime.fromISO(newItem.endDate),
                );
            } else {
                newItem.countDay = getDiffDayFromDateString(newItem.startDate, newItem.endDate) + 1;
            }
        }

        newOldItems[mappingItemIdIndex[newItem.id]] = newItem;
        if (
            ['left', 'right', 'both'].includes(dragging) &&
            newItem.bondRight &&
            (!directionChanges || directionChanges === 'right')
        ) {
            const diffDayBonded = getDiffDayFromDateString(oldItem.endDate, newItem.endDate);
            if (['right', 'both'].includes(dragging)) {
                moveBondedItem(newItem.bondRight, diffDayBonded);
                moveBondedByTypeItem(newItem, newItem.bondParentEE, 'EE');
                moveBondedByTypeItem(newItem, newItem.bondParentES, 'ES');
            }

            if (['left', 'both'].includes(dragging)) {
                moveBondedByTypeItem(newItem, newItem.bondParentSE, 'SE');
                moveBondedByTypeItem(newItem, newItem.bondParentSS, 'SS');
            }
        }

        if (newItem.parentId && (!directionChanges || directionChanges === 'up')) {
            newOldItems = moveParent(newItem.parentId);
        }

        return newOldItems;
    }

    return newOldItems;
};

export const processOrderingItemsAndSetNumbersWBS = (inputItems: ItemType[]) => {
    const oldMappingScheduleItemIdIndex = getMappingIdIndex(inputItems);
    const listProjectItem = inputItems.filter((item) => item.roleItem === 'project');

    const newInputItems: ItemType[] = [];
    const newItems = [];

    let commonIndex = 0;
    const processChildren = (level: string, children?: string[]) => {
        if (!children) {
            return;
        }

        let index = 1;
        for (const childId of children) {
            const oldItem = inputItems[oldMappingScheduleItemIdIndex[childId]];

            if (!oldItem) {
                continue;
            }

            newInputItems.push({ ...oldItem, idIndex: !oldItem.hideForCollapse ? commonIndex : -1 });

            const newNumberWBS = `${level ? level + '.' : ''}${index++}`;

            const newItem: ItemType = {
                ...oldItem,
                numberWBS: newNumberWBS,
                idIndex: !oldItem.hideForCollapse ? commonIndex++ : -1,
            };
            newItems.push(newItem);

            processChildren(newNumberWBS, oldItem.children);
        }
    };

    for (const projectItem of listProjectItem) {
        const newProjectItem = { ...projectItem, idIndex: !projectItem.hideForCollapse ? commonIndex++ : -1 };
        newInputItems.push(newProjectItem);
        newItems.push(newProjectItem);

        processChildren('', projectItem.children);
    }

    const mappingScheduleItemIdIndex = getMappingIdIndex(newItems);
    return { mappingScheduleItemIdIndex, newInputItems, newItems };
};

export const processCompletionPercentage = (
    inputItems: ItemType[],
    statusTaskDone: string,
    mappingScheduleItemIdIndex: { [key: string]: number },
) => {
    const listItemChangedCompletionPercentage: ItemType[] = [];

    let newItemsWithPercentage = inputItems.map((item) => {
        if (item.children?.length) {
            return item;
        }

        const listTaskDone = (item.tasks || []).filter((task) => task.status?.toString() === statusTaskDone);

        const newCompletionPercentage = item.tasks?.length
            ? Math.round((listTaskDone.length / item.tasks.length) * 100)
            : item.completionPercentage;

        const newItem = {
            ...item,
            completionPercentage: newCompletionPercentage,
            status: calcStatusByPercentageCompletion(newCompletionPercentage),
        };

        if (item.completionPercentage !== newCompletionPercentage) {
            listItemChangedCompletionPercentage.push(newItem);
        }

        return newItem;
    });

    for (const itemChangedCompletionPercentage of listItemChangedCompletionPercentage) {
        newItemsWithPercentage = changePercentageItemByPercent(
            mappingScheduleItemIdIndex,
            itemChangedCompletionPercentage.completionPercentage,
            itemChangedCompletionPercentage,
            newItemsWithPercentage,
        );
    }

    return newItemsWithPercentage;
};
